import { useEffect, useMemo } from 'react'
import { MenuItem, ListItemText, Skeleton } from '@mui/material'
import { OverlayState } from '@/hooks/useOverlay'
import { useGetAuthRoles } from '@/service-library/hooks/auth-user'
import { isRoleVisibleInUI, DEFAULT_USER_ROLE } from '@/utils/auth-user'
import { showErrorSnackbar } from '@/utils/snackbars'
import FormSelect from '@/components/forms/FormSelect'

type UserRolesPickerProps = {
  overlay?: OverlayState
}

export default function UserRolesPicker({ overlay }: UserRolesPickerProps) {
  const { roles, isError, isPending } = useGetAuthRoles()

  const assignableRolesIds = useMemo(
    () => roles.filter((role) => role.is_assignable).map((role) => role.id),
    [roles],
  )

  useEffect(() => {
    if (overlay?.isOpen && isError) {
      overlay.close()
      showErrorSnackbar('Something went wrong. Please try again later.')
    }
  }, [isError, overlay])

  return isPending ? (
    <Skeleton variant="rounded" height={50} />
  ) : (
    <FormSelect
      name="role_id"
      label="Role"
      variant="filled"
      selectProps={{
        renderValue: (value) => <>{value}</>,
      }}
      helperText="You can only assign roles equal to or less than your own."
    >
      <MenuItem key={DEFAULT_USER_ROLE.id} value={DEFAULT_USER_ROLE.id}>
        <ListItemText
          primary={DEFAULT_USER_ROLE.id}
          secondary={DEFAULT_USER_ROLE.description}
        />
      </MenuItem>
      {roles.map((role) => {
        if (!isRoleVisibleInUI(role.id) || role.id === 'Prime Admin')
          return null
        const canAssign = assignableRolesIds.includes(role.id)
        return (
          <MenuItem key={role.id} value={role.id} disabled={!canAssign}>
            <ListItemText primary={role.id} secondary={role.description} />
          </MenuItem>
        )
      })}
    </FormSelect>
  )
}
